import { ResumeSectionType } from '@applicator/shared'

import { Sentry, logger } from './sentry'
import { supabase } from './supabase'

export type SaveResumeContentResult =
  | { ok: true }
  | { ok: false; error: string }

export type ResumePersistFailureContext = {
  resumeId: string
  sectionType: ResumeSectionType | string
  error: string
  attempts: number
}

export function logResumePersistFailure(ctx: ResumePersistFailureContext): void {
  const extra = {
    resume_id: ctx.resumeId,
    section_type: ctx.sectionType,
    error: ctx.error,
    attempts: ctx.attempts,
  }
  logger.warn('Resume section persist failed', extra)
  Sentry.captureMessage('Resume section persist failed', {
    level: 'error',
    extra,
    tags: { feature: 'resume', operation: 'saveResumeContent' },
  })
}

const MAX_PERSIST_ATTEMPTS = 2

export async function persistResumeSection(
  resumeId: string,
  sectionType: ResumeSectionType,
  content: Record<string, unknown>,
  displayOrder: number,
): Promise<SaveResumeContentResult> {
  let lastError = 'Unknown persist failure'

  for (let attempt = 1; attempt <= MAX_PERSIST_ATTEMPTS; attempt++) {
    const { error: upsertError } = await supabase.from('resume_content').upsert(
      {
        resume_id: resumeId,
        section_type: sectionType,
        content,
        display_order: displayOrder,
      },
      {
        onConflict: 'resume_id,section_type',
        ignoreDuplicates: false,
      },
    )

    if (upsertError) {
      lastError = upsertError.message
      if (attempt < MAX_PERSIST_ATTEMPTS) continue
      logResumePersistFailure({
        resumeId,
        sectionType,
        error: lastError,
        attempts: attempt,
      })
      return { ok: false, error: lastError }
    }

    const { data: verifyRow, error: verifyError } = await supabase
      .from('resume_content')
      .select('section_type')
      .eq('resume_id', resumeId)
      .eq('section_type', sectionType)
      .maybeSingle()

    if (verifyError) {
      lastError = verifyError.message
      if (attempt < MAX_PERSIST_ATTEMPTS) continue
      logResumePersistFailure({
        resumeId,
        sectionType,
        error: lastError,
        attempts: attempt,
      })
      return { ok: false, error: lastError }
    }

    if (!verifyRow?.section_type) {
      lastError = 'Persist verify failed: section row missing after upsert'
      if (attempt < MAX_PERSIST_ATTEMPTS) continue
      logResumePersistFailure({
        resumeId,
        sectionType,
        error: lastError,
        attempts: attempt,
      })
      return { ok: false, error: lastError }
    }

    return { ok: true }
  }

  logResumePersistFailure({
    resumeId,
    sectionType,
    error: lastError,
    attempts: MAX_PERSIST_ATTEMPTS,
  })
  return { ok: false, error: lastError }
}
